import { useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import { WDAC_TABS_DATA_COUNTS_KEY } from 'src/constants/query';
import {
  WDAC_RESULTS_QUERY_KEY,
  invalidateWin32AppImportQueries,
  postGlobalAppStatus,
  statusUpdateMovesImports,
} from 'src/hooks/wdac';
import { getStatusSavedMessage } from './Win32AppStatusModal';
import { pagesText } from 'src/constants/pagesText';
import parseError from 'src/utils/parseError';
import { APPLICATION_TRUST_STATUS } from 'src/utils/enums/applicationTrustStatus';

/**
 * Hook to update a WDAC result's status: updates note always, removes item locally only when status changes, then refetches.
 * @param {Function} onSuccessCallback - callback to run after successful update (e.g., close modal)
 * @param {boolean} hasFilters - whether filters/search are active
 * @param {object} query - the current query key object for fetching results
 * @param {string|number} customerEnvId - customer's environment ID
 */
export default function useUpdateWDACStatus(onSuccessCallback, hasFilters, query, customerEnvId) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation(postGlobalAppStatus, {
    onMutate: async ({ globalApplicationVersionId, status, notes }) => {
      // cancel ongoing queries
      await queryClient.cancelQueries([WDAC_TABS_DATA_COUNTS_KEY]);
      await queryClient.cancelQueries([WDAC_RESULTS_QUERY_KEY, query, customerEnvId]);

      // snapshot
      const previousCounts = queryClient.getQueryData([WDAC_TABS_DATA_COUNTS_KEY]);
      const previousResults = queryClient.getQueryData([
        WDAC_RESULTS_QUERY_KEY,
        query,
        customerEnvId,
      ]);

      // find old status
      let oldStatus;
      if (previousResults) {
        for (const page of previousResults.pages) {
          const found = page.results.find(r => r.id === globalApplicationVersionId);
          if (found) {
            oldStatus = found.status;
            break;
          }
        }
      }
      const statusChanged = status !== oldStatus;

      // decrement count only on status change and when no filters
      if (statusChanged && !hasFilters) {
        queryClient.setQueryData([WDAC_TABS_DATA_COUNTS_KEY], prev =>
          prev ? { ...prev, win32AppsCount: Math.max(prev.win32AppsCount - 1, 0) } : prev,
        );
      }

      // update or remove in results pages
      queryClient.setQueryData([WDAC_RESULTS_QUERY_KEY, query, customerEnvId], old => {
        if (!old) return old;
        const now = new Date().toISOString();
        const pages = old.pages.map(page => ({
          ...page,
          results: page.results
            .filter(row => !(statusChanged && row.id === globalApplicationVersionId))
            .map(row =>
              row.id === globalApplicationVersionId
                ? {
                    ...row,
                    notes,
                    trustUpdatedOn: status === APPLICATION_TRUST_STATUS.Trusted ? now : null,
                  }
                : row,
            ),
        }));
        return { ...old, pages };
      });

      return { previousCounts, previousResults };
    },
    onError: (error, _vars, context) => {
      // rollback
      if (!hasFilters && context?.previousCounts) {
        queryClient.setQueryData([WDAC_TABS_DATA_COUNTS_KEY], context.previousCounts);
      }
      if (context?.previousResults) {
        queryClient.setQueryData(
          [WDAC_RESULTS_QUERY_KEY, query, customerEnvId],
          context.previousResults,
        );
      }
      enqueueSnackbar(parseError(error) || pagesText.somethingWentWrong, {
        variant: 'error',
        autoHideDuration: 3000,
      });
    },
    onSuccess: (_data, variables) => {
      if (statusUpdateMovesImports(variables)) {
        invalidateWin32AppImportQueries(queryClient);
      }
      enqueueSnackbar(getStatusSavedMessage(variables), {
        variant: 'success',
        autoHideDuration: 3000,
      });
      if (typeof onSuccessCallback === 'function') {
        onSuccessCallback();
      }
    },
    // Invalidated by prefix rather than by this exact query, so the overview's counts, which are
    // their own filtered reads of the same endpoint, refresh alongside the list.
    onSettled: () => {
      queryClient.invalidateQueries([WDAC_TABS_DATA_COUNTS_KEY]);
      queryClient.invalidateQueries([WDAC_RESULTS_QUERY_KEY]);
    },
  });
}
